import { useState, type FormEvent } from 'react'
import { Loader2, Sparkles, Undo2, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'

import { interpretFilters } from '../services/explorerService'
import type {
	ExplorerFilter,
	ExplorerGrainKey,
	ExplorerInterpretChip,
	ExplorerReportScope,
	ExplorerSort,
} from '../types/explorer.types'

/** The parts of a definition the box reads and sets. */
export interface AskFilterLayout {
	columns: string[]
	filters: ExplorerFilter[]
	sorts: ExplorerSort[]
	groupBy: string | null
}

interface AskFilterInputProps {
	grain: ExplorerGrainKey
	layout: AskFilterLayout & { scope: ExplorerReportScope | null }
	/** Apply to the live definition; nothing is saved until the user saves. */
	onApply: (layout: AskFilterLayout) => void
	/** The request, injectable for tests. */
	interpret?: typeof interpretFilters
}

interface Applied {
	chips: ExplorerInterpretChip[]
	/** The layout before the answer, for Undo. */
	before: AskFilterLayout
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

const messageOf = (error: any): string =>
	error?.response?.data?.message || "Ask MDT couldn't answer just now. Try again in a moment."

/**
 * Ask for the rows in plain words (MYD-628): "open tasks due this month
 * assigned to Mary, grouped by project". The server works out the filters,
 * sorts and grouping, checked against this user's own columns, and they are
 * applied to the grid as if set by hand: the filter chips, the group control
 * and the column headers all show them, and nothing is saved until the user
 * saves.
 *
 * What it set comes back as chips, worded by the server from the checked
 * result. Removing a chip takes back that one change; Undo takes back the lot.
 */
export function AskFilterInput({ grain, layout, onApply, interpret = interpretFilters }: AskFilterInputProps) {
	const [text, setText] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [note, setNote] = useState<string | null>(null)
	const [applied, setApplied] = useState<Applied | null>(null)

	const current: AskFilterLayout = { columns: layout.columns, filters: layout.filters, sorts: layout.sorts, groupBy: layout.groupBy }

	const submit = async (event: FormEvent) => {
		event.preventDefault()
		const said = text.trim()
		if (!said || busy) return
		setBusy(true)
		setError(null)
		setNote(null)
		try {
			const result = await interpret({ grain, text: said, currentDefinition: { ...current, scope: layout.scope } })
			setNote(result.unsupported)
			if (same(result.definition, current)) {
				if (!result.unsupported) setNote('That is what the table already shows.')
				return
			}
			onApply(result.definition)
			setApplied({ chips: result.explanation, before: current })
			setText('')
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setBusy(false)
		}
	}

	/** Take back one change: that filter, that sort, or the grouping. */
	const removeChip = (chip: ExplorerInterpretChip) => {
		if (chip.kind === 'filter') onApply({ ...current, filters: current.filters.filter(filter => filter.key !== chip.key) })
		if (chip.kind === 'sort') onApply({ ...current, sorts: current.sorts.filter(sort => sort.key !== chip.key) })
		if (chip.kind === 'group' && current.groupBy === chip.key) onApply({ ...current, groupBy: null })
		setApplied(prev => (prev ? { ...prev, chips: prev.chips.filter(other => other !== chip) } : prev))
	}

	const undo = () => {
		if (!applied) return
		onApply(applied.before)
		setApplied(null)
		setNote(null)
	}

	return (
		<div className="mb-3 space-y-2">
			<form onSubmit={submit} className="flex items-center gap-2" role="search" aria-label="Ask for the rows you want">
				<div className="relative flex-1 max-w-2xl">
					<Sparkles className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" aria-hidden="true" />
					<Input
						value={text}
						onChange={event => setText(event.target.value)}
						placeholder='Ask for rows, e.g. "open tasks due this month, grouped by project"'
						aria-label="Describe the rows you want"
						maxLength={300}
						disabled={busy}
						className="pl-9"
					/>
				</div>
				<Button type="submit" variant="primary" disabled={busy || !text.trim()}>
					{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
					{busy ? 'Working…' : 'Apply'}
				</Button>
			</form>

			{error && (
				<p role="alert" className="text-xs text-destructive">
					{error}
				</p>
			)}
			{note && <p className="text-xs text-gray-600">{note}</p>}

			{applied && (
				<div className="flex flex-wrap items-center gap-1.5" aria-label="What Ask MDT set">
					{applied.chips.map(chip => (
						<span
							key={`${chip.kind}:${chip.key}`}
							className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs text-gray-800"
						>
							{chip.text}
							<button
								type="button"
								onClick={() => removeChip(chip)}
								aria-label={`Remove ${chip.text}`}
								className="rounded-full text-gray-500 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							>
								<X className="h-3 w-3" aria-hidden="true" />
							</button>
						</span>
					))}
					<Button type="button" variant="text" size="input" onClick={undo}>
						<Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
						Undo
					</Button>
				</div>
			)}
		</div>
	)
}

export default AskFilterInput
